import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Trash2, CheckCircle2, XCircle, MessageSquareReply, Loader2, X } from 'lucide-react';
import StarRating from '../StarRating';
import { resolveImageUrl } from '../../utils/imageUrl';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReviewsManagement = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // all | approved | rejected
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [savingReply, setSavingReply] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const params = statusFilter === 'all' ? {} : { status: statusFilter };
      const res = await axios.get(`${API}/admin/reviews`, { ...getAuthHeaders(), params });
      setReviews(res.data);
    } catch (err) {
      console.error('Failed to fetch reviews', err);
      toast({ title: 'Failed to load reviews', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, [statusFilter]);

  const stats = useMemo(() => {
    const total = reviews.length;
    const approved = reviews.filter((r) => r.status === 'approved').length;
    const rejected = reviews.filter((r) => r.status === 'rejected').length;
    const avg = total ? reviews.reduce((a, r) => a + (r.rating || 0), 0) / total : 0;
    return { total, approved, rejected, avg };
  }, [reviews]);

  const setStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}`, { status }, getAuthHeaders());
      toast({ title: `Review ${status === 'approved' ? 'approved' : 'rejected'}` });
      fetchReviews();
    } catch (err) {
      toast({ title: 'Failed to update review', variant: 'destructive' });
    }
  };

  const deleteReview = async (id) => {
    if (!window.confirm('Permanently delete this review? Customers will no longer see it.')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, getAuthHeaders());
      toast({ title: 'Review deleted' });
      fetchReviews();
    } catch (err) {
      toast({ title: 'Failed to delete review', variant: 'destructive' });
    }
  };

  const openReply = (r) => {
    setReplyingId(r.id);
    setReplyText(r.admin_reply || '');
  };

  const saveReply = async () => {
    if (!replyingId) return;
    setSavingReply(true);
    try {
      await axios.put(
        `${API}/admin/reviews/${replyingId}`,
        { admin_reply: replyText },
        getAuthHeaders()
      );
      toast({ title: 'Reply saved' });
      setReplyingId(null);
      setReplyText('');
      fetchReviews();
    } catch (err) {
      toast({ title: 'Failed to save reply', variant: 'destructive' });
    } finally {
      setSavingReply(false);
    }
  };

  return (
    <div data-testid="admin-reviews-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold">Reviews Management</h2>
          <p className="text-sm text-gray-600">Approve, reject, delete or reply to customer reviews.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {['all', 'approved', 'rejected'].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold capitalize border transition ${
                statusFilter === s
                  ? 'bg-yellow-500 text-black border-yellow-500'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
              }`}
              data-testid={`review-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow border">
          <p className="text-xs text-gray-500">Total (filtered)</p>
          <p className="text-2xl font-bold" data-testid="reviews-total">{stats.total}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow border">
          <p className="text-xs text-gray-500">Approved</p>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow border">
          <p className="text-xs text-gray-500">Rejected</p>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow border">
          <p className="text-xs text-gray-500">Avg. rating</p>
          <div className="flex items-center gap-2">
            <p className="text-2xl font-bold text-yellow-600">{stats.avg.toFixed(1)}</p>
            <StarRating value={stats.avg} size={16} testIdPrefix="admin-avg-star" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-500"></div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded-lg text-gray-500">
          No reviews found.
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div
              key={r.id}
              className="bg-white rounded-lg shadow border p-4 sm:p-5"
              data-testid={`admin-review-item-${r.id}`}
            >
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StarRating value={r.rating} size={16} testIdPrefix={`admin-rev-${r.id}-star`} />
                    <h3 className="font-bold text-gray-900">{r.title}</h3>
                    <span
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        r.status === 'approved'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {r.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    By <span className="font-semibold">{r.customer_name}</span> ({r.customer_email}) · Product ID:{' '}
                    <code className="bg-gray-100 px-1 rounded">{r.product_id}</code>
                  </p>
                  <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{r.message}</p>

                  {r.images && r.images.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {r.images.map((url) => (
                        <button
                          key={url}
                          type="button"
                          onClick={() => setPreviewImg(resolveImageUrl(url))}
                          className="w-14 h-14 rounded overflow-hidden border hover:opacity-90"
                        >
                          <img src={resolveImageUrl(url)} alt="review" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}

                  {r.admin_reply && r.admin_reply.trim() && (
                    <div className="mt-3 bg-yellow-50 border-l-4 border-yellow-500 p-2.5 rounded">
                      <p className="text-xs font-bold text-yellow-800 mb-1">Your reply:</p>
                      <p className="text-sm text-gray-700 whitespace-pre-line">{r.admin_reply}</p>
                    </div>
                  )}
                </div>

                <div className="flex md:flex-col gap-2">
                  {r.status !== 'approved' && (
                    <button
                      onClick={() => setStatus(r.id, 'approved')}
                      className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
                      data-testid={`approve-review-${r.id}`}
                    >
                      <CheckCircle2 size={16} /> Approve
                    </button>
                  )}
                  {r.status !== 'rejected' && (
                    <button
                      onClick={() => setStatus(r.id, 'rejected')}
                      className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
                      data-testid={`reject-review-${r.id}`}
                    >
                      <XCircle size={16} /> Reject
                    </button>
                  )}
                  <button
                    onClick={() => openReply(r)}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
                    data-testid={`reply-review-${r.id}`}
                  >
                    <MessageSquareReply size={16} /> Reply
                  </button>
                  <button
                    onClick={() => deleteReview(r.id)}
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg text-sm font-semibold flex items-center gap-1"
                    data-testid={`delete-review-${r.id}`}
                  >
                    <Trash2 size={16} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply modal */}
      {replyingId && (
        <div
          className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
          onClick={() => !savingReply && setReplyingId(null)}
        >
          <div
            className="bg-white rounded-lg p-5 w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
            data-testid="reply-modal"
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Reply to Review</h3>
              <button onClick={() => setReplyingId(null)} className="text-gray-500 hover:text-gray-700">
                <X />
              </button>
            </div>
            <textarea
              rows={5}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write a public response to this review..."
              className="w-full border rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              data-testid="reply-textarea"
            />
            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={() => setReplyingId(null)}
                className="border border-gray-300 hover:bg-gray-100 px-4 py-2 rounded-lg"
                disabled={savingReply}
              >
                Cancel
              </button>
              <button
                onClick={saveReply}
                disabled={savingReply}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-4 py-2 rounded-lg flex items-center gap-2 disabled:opacity-60"
                data-testid="save-reply-btn"
              >
                {savingReply && <Loader2 className="animate-spin" size={16} />}
                Save Reply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {previewImg && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setPreviewImg(null)}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full p-2 hover:bg-black"
            onClick={() => setPreviewImg(null)}
            aria-label="Close preview"
          >
            <X />
          </button>
          <img src={previewImg} alt="Preview" className="max-h-[85vh] max-w-full rounded-lg" />
        </div>
      )}
    </div>
  );
};

export default ReviewsManagement;
